import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { Building2, Save, RefreshCw, Pencil, X, Check, Minus } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { HOSPITAL_PROFILE_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * Hospital discovery data the sales team collects, stored in its own table.
 *
 * The form is built from the field descriptors the API returns, so which
 * fields appear is the tenant's setting (Settings → Hospital Profile) and not
 * a hardcoded list here. Fields switched off still arrive for leads that
 * already answered them, flagged `is_retired`, and render read-only.
 */
const HospitalProfilePanel = ({ customerId }) => {
    const { fetch, create } = useApiMethods();

    const [groups, setGroups]   = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving]   = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState({});

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(`${HOSPITAL_PROFILE_URL}?customer_id=${customerId}`)
            .then(res => {
                if (res?.status === "success") {
                    setGroups(res.groups || []);
                    const seeded = {};
                    (res.data || []).forEach(f => {
                        seeded[f.key] = f.value ?? (f.type === "multi" ? [] : "");
                    });
                    setDraft(seeded);
                } else {
                    ToastMsg("error", res?.message || "Failed to load hospital details");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load hospital details"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    useEffect(() => { load(); }, [load]);

    const allFields = useMemo(
        () => groups.flatMap(g => g.fields || []), [groups]);

    const setField = (key, value) => setDraft(d => ({ ...d, [key]: value }));

    const save = () => {
        const payload = {};
        allFields.forEach(f => {
            // Retired fields are shown but not collected. Submitting one is
            // rejected server-side, and validation is all-or-nothing — it would
            // fail the whole save over a field nobody touched.
            if (f.is_retired) return;
            const v = draft[f.key];
            const empty = v === "" || v === null || v === undefined ||
                          (Array.isArray(v) && v.length === 0);
            if (!empty || f.has_value) payload[f.key] = v;
        });
        if (!Object.keys(payload).length) { setEditing(false); return; }

        setSaving(true);
        create(HOSPITAL_PROFILE_URL, { customer_id: customerId, values: payload })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Hospital details saved");
                    setEditing(false);
                    setGroups(res.groups || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to save");
                }
            })
            .catch(() => ToastMsg("error", "Failed to save"))
            .finally(() => setSaving(false));
    };

    // ── read-only rendering of one answer ──
    const displayValue = (f) => {
        const v = f.value;
        const blank = <span className="text-gray-300 dark:text-gray-600">—</span>;
        if (v === null || v === undefined || v === "") return blank;

        if (f.type === "boolean") {
            return (
                <span className={cn(
                    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium",
                    v ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                      : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                )}>
                    {v ? <Check className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
                    {v ? "Yes" : "No"}
                </span>
            );
        }
        if (f.type === "multi") {
            if (!v.length) return blank;
            return (
                <span className="flex flex-wrap gap-1 justify-end">
                    {v.map(s => (
                        <span key={s} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">
                            {s}
                        </span>
                    ))}
                </span>
            );
        }
        return (
            <>{String(v)}{f.unit && <span className="text-gray-400 ml-0.5">{f.unit}</span>}</>
        );
    };

    // ── editable control for one field ──
    const editor = (f) => {
        const v = draft[f.key];
        const base = "w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-400";

        if (f.type === "boolean") {
            return (
                <Select value={v === "" || v === null ? undefined : !!v}
                    onChange={val => setField(f.key, val)}
                    className="w-full" size="small" placeholder="Not asked" allowClear
                    onClear={() => setField(f.key, "")}
                    options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
            );
        }
        if (f.type === "choice") {
            return (
                <Select value={v || undefined} onChange={val => setField(f.key, val ?? "")}
                    className="w-full" size="small" allowClear showSearch
                    placeholder={`Select ${f.label}`}
                    options={(f.options || []).map(o => ({ value: o, label: o }))} />
            );
        }
        if (f.type === "multi") {
            return (
                <Select mode="multiple" value={v || []} onChange={val => setField(f.key, val || [])}
                    className="w-full" size="small" allowClear showSearch
                    placeholder={`Select ${f.label}`}
                    options={(f.options || []).map(o => ({ value: o, label: o }))} />
            );
        }
        if (f.type === "textarea") {
            return <textarea rows={3} value={v || ""} onChange={e => setField(f.key, e.target.value)}
                className={cn(base, "resize-none")} placeholder={f.help_text} />;
        }
        if (f.type === "number") {
            return (
                <div className="flex items-center gap-1">
                    <input type="number" min="0" step="1" value={v ?? ""}
                        onChange={e => setField(f.key, e.target.value)} className={base} />
                    {f.unit && <span className="text-[10px] text-gray-400 whitespace-nowrap">{f.unit}</span>}
                </div>
            );
        }
        return <input type="text" value={v || ""} onChange={e => setField(f.key, e.target.value)}
            className={base} placeholder={f.help_text} />;
    };

    if (!customerId) return null;

    if (loading && !groups.length) {
        return (
            <div className="flex items-center gap-2 p-4">
                <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Loading hospital details…</span>
            </div>
        );
    }

    if (!allFields.length) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" /> Hospital Profile
                </p>
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    Every field is switched off. A super admin can turn them back on under
                    Settings → Hospital Profile.
                </p>
            </div>
        );
    }

    const liveFields  = allFields.filter(f => !f.is_retired);
    const filledCount = liveFields.filter(f => f.has_value).length;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                    <Building2 className="h-4 w-4 text-indigo-500" />
                    Hospital Profile
                    <span className="font-normal text-gray-400">
                        {filledCount}/{liveFields.length} filled
                    </span>
                </span>
                {editing ? (
                    <div className="flex items-center gap-1">
                        <button disabled={saving} onClick={save}
                            className="h-7 px-2.5 rounded-md text-[11px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                            <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={() => { setEditing(false); load(); }}
                            className="h-7 w-7 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center">
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <button onClick={() => setEditing(true)}
                        className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
                        <Pencil className="h-3 w-3" /> Edit
                    </button>
                )}
            </div>

            {groups.map(group => (
                <div key={group.group}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                    <p className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                        {group.group}
                    </p>
                    <div className="p-3 space-y-2">
                        {(group.fields || []).map(f => (
                            <div key={f.key} className={cn(
                                editing && !f.is_retired
                                    ? "space-y-1"
                                    : "flex items-start justify-between gap-3"
                            )}>
                                <label className="text-[11px] text-gray-500 dark:text-gray-400 flex-shrink-0"
                                    title={f.is_retired
                                        ? "No longer collected — kept because this lead already answered"
                                        : (f.help_text || undefined)}>
                                    {f.label}
                                    {f.is_retired && (
                                        <span className="ml-1 text-[9px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400">
                                            archived
                                        </span>
                                    )}
                                </label>
                                {editing && !f.is_retired ? editor(f) : (
                                    <span className={cn(
                                        "text-[11px] font-medium text-right",
                                        f.is_retired
                                            ? "text-gray-400 dark:text-gray-500"
                                            : "text-gray-800 dark:text-gray-200"
                                    )}>
                                        {displayValue(f)}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default HospitalProfilePanel;
